import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Callback route del flow Google OAuth 2.0 diretto.
 * Google reindirizza qui con `?code=xxx&state=yyy` (o `?error=xxx`).
 * Scambiamo il code col backend, che verifica id_token e ci ritorna user o needs_registration.
 */
export default function GoogleCallback() {
  const nav = useNavigate();
  const { completeGoogleCode } = useAuth();
  const ranRef = useRef(false); // evita double-run in StrictMode

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const err = params.get("error");

    if (err) {
      toast.error(`Google OAuth errore: ${err}`);
      nav("/login", { replace: true });
      return;
    }
    if (!code) {
      toast.error("Codice Google mancante");
      nav("/login", { replace: true });
      return;
    }
    const expectedState = sessionStorage.getItem("ht_google_oauth_state");
    sessionStorage.removeItem("ht_google_oauth_state");
    if (!expectedState || params.get("state") !== expectedState) {
      toast.error("Accesso Google non valido o scaduto. Riprova.");
      nav("/login", { replace: true });
      return;
    }

    // Iter209el — Registrazione Google avviata da un link invito.
    let invite = null;
    try { invite = JSON.parse(sessionStorage.getItem("ht_google_invite") || "null"); } catch { invite = null; }
    sessionStorage.removeItem("ht_google_invite");
    const extra = invite?.token
      ? { invite_token: invite.token, phone: invite.phone || "", recovery_email: invite.recovery_email || "" }
      : {};

    completeGoogleCode(code, extra)
      .then((r) => {
        if (r?.needs_invite_details && r.invite_token) {
          sessionStorage.setItem("ht_google_invite_details", JSON.stringify({
            token: r.invite_token, pending_token: r.pending_token, missing: r.missing || [], google: r.google || {},
          }));
          nav(`/join/${r.invite_token}`, { replace: true });
          return;
        }
        if (r?.needs_registration && r.google) {
          sessionStorage.setItem("ht_google_prefill", JSON.stringify({
            email: r.google.email,
            first_name: r.google.first_name || "",
            last_name: r.google.last_name || "",
            picture: r.google.picture || "",
            // pending_token: JWT firmato server-side (15 min) che attesta la verifica
            // dell'id_token Google. Il form /register lo invierà a /auth/register per
            // dimostrare che l'email è effettivamente stata verificata da Google.
            pending_token: r.pending_token || "",
            oauth_provider: "google",
          }));
          toast.info("Completa i dati per finalizzare la registrazione");
          nav("/register?provider=google", { replace: true });
          return;
        }
        toast.success(`Benvenuto ${r?.first_name || r?.email || ""}`);
        nav("/dashboard", { replace: true });
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail || "Errore autenticazione Google";
        const status = e?.response?.status;
        if (invite?.token) {
          toast.error(detail);
          nav(`/join/${invite.token}`, { replace: true });
        } else if (status === 403) {
          toast.info(detail);
          nav("/pending", { replace: true });
        } else {
          toast.error(detail);
          nav("/login", { replace: true });
        }
      });
  }, [completeGoogleCode, nav]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-purple-400 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-4 text-sm text-muted-foreground">Completamento accesso Google in corso…</p>
      </div>
    </div>
  );
}
